import { Body, Camera, Scenario } from '../types.js';
import { makeArcadeBody, createSolarSystemScenario } from '../physics/bodies.js';
import { Renderer } from '../rendering/renderer.js';

// ─── HUD drawing ───

function drawHUD(ctx: CanvasRenderingContext2D, w: number, h: number, state: {
  paused: boolean;
  timeScale: number;
  bodyCount: number;
  showHelp: boolean;
  selectedScenario: string;
}) {
  const fontSize = Math.max(12, Math.min(16, w * 0.012));

  // Top bar
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(0, 0, w, 44);

  ctx.font = `${fontSize}px monospace`;
  ctx.textBaseline = 'middle';

  // Title
  ctx.fillStyle = '#aabbff';
  ctx.fillText('⟐ ORBITAL SIM', 16, 22);

  // Body count
  ctx.fillStyle = '#667799';
  ctx.fillText(`${state.bodyCount} bodies`, w * 0.35, 22);

  // Time scale
  ctx.fillStyle = state.paused ? '#ff8844' : '#88ddaa';
  ctx.fillText(`⏱ ${state.paused ? 'PAUSED' : '1x'}`, w * 0.55, 22);

  // Zoom indicator
  ctx.fillStyle = '#667799';
  ctx.fillText(`🔍 ${state.timeScale.toFixed(1)}x`, w * 0.75, 22);

  // Scenario name
  ctx.fillStyle = '#ffdd66';
  const scenarioText = `▶ ${state.selectedScenario}`;
  ctx.fillText(scenarioText, w - 16 - ctx.measureText(scenarioText).width, 22);

  // Help overlay
  if (state.showHelp) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, w, h);

    const helpY = h * 0.15;
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${fontSize * 1.5}px monospace`;
    ctx.fillText('Orbital Sim — Controls', w * 0.5 - 100, helpY);

    ctx.font = `${fontSize}px monospace`;
    ctx.fillStyle = '#aabbcc';
    const lines = [
      '🖱️  Click & drag to place a new body',
      '🖱️  Drag while placing to set velocity',
      '🖱️  Scroll to zoom in/out',
      '🖱️  Right-click drag to pan',
      '⌨️  Space — pause / resume',
      '⌨️  R — reset to current scenario',
      '⌨️  H — toggle this help',
      '⌨️  + / - — adjust time speed',
    ];
    lines.forEach((line, i) => {
      ctx.fillText(line, w * 0.5 - 250, helpY + 40 + i * 28);
    });

    ctx.fillStyle = '#667799';
    ctx.fillText('Click anywhere to dismiss', w * 0.5 - 80, h * 0.7);
  }
}

// ─── Scenario management ───

const SCENARIOS: { name: string; create: () => Body[] }[] = [
  { name: 'Solar System', create: createSolarSystemScenario },
  { name: 'Binary Stars', create: () => {
    const bodies: Body[] = [];
    const s1 = makeArcadeBody(100, 0, 0, 1.5, 300, 22, true);
    s1.name = 'Alpha';
    s1.color = '#ffaa44';
    s1.glowColor = '#ffcc6680';
    bodies.push(s1);

    const s2 = makeArcadeBody(-100, 0, 0, -1.5, 300, 22, true);
    s2.name = 'Beta';
    s2.color = '#44aaff';
    s2.glowColor = '#66ccff80';
    bodies.push(s2);

    // Orbiting bodies
    bodies.push(makeArcadeBody(250, 0, 0, 1.1, 30, 10));
    bodies[2].name = 'Orbiter A';

    bodies.push(makeArcadeBody(-200, 100, -0.8, -0.4, 20, 8));
    bodies[3].name = 'Orbiter B';

    return bodies;
  }},
  { name: 'Random Chaos', create: () => {
    const bodies: Body[] = [];
    // Central massive body
    const center = makeArcadeBody(0, 0, 0, 0, 400, 25, true);
    center.name = 'Core';
    center.color = '#ff8844';
    center.glowColor = '#ffaa6680';
    bodies.push(center);

    for (let i = 0; i < 12; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 120 + Math.random() * 500;
      const speed = 0.5 + Math.random() * 2;
      const mx = Math.cos(angle) * dist;
      const my = Math.sin(angle) * dist;
      bodies.push({
        id: 0,
        x: mx, y: my,
        vx: -Math.sin(angle) * speed,
        vy: Math.cos(angle) * speed,
        ax: 0, ay: 0,
        mass: 5 + Math.random() * 50,
        radius: 3 + Math.random() * 10,
        color: `hsl(${Math.random() * 360}, 80%, 60%)`,
        glowColor: `hsla(${Math.random() * 360}, 80%, 60%, 0.5)`,
        name: 'Body ' + i,
        trail: [],
        maxTrail: 300,
        isStar: false,
      });
    }
    return bodies;
  }},
  { name: 'Figure Eight', create: () => {
    // Famous 3-body figure-8 orbit
    const v = 0.93240737;
    const b1 = makeArcadeBody(97, 0, -0.466 * v, 0.433 * v, 100, 12);
    b1.name = 'X';
    b1.color = '#ff4488';
    b1.glowColor = '#ff66aa80';

    const b2 = makeArcadeBody(-97, 0, -0.466 * v, 0.433 * v, 100, 12);
    b2.name = 'Y';
    b2.color = '#44ff88';
    b2.glowColor = '#66ffaa80';

    const b3 = makeArcadeBody(0, 0, 0.932 * v, -0.866 * v, 100, 12);
    b3.name = 'Z';
    b3.color = '#4488ff';
    b3.glowColor = '#66aaff80';

    return [b1, b2, b3];
  }},
];

// Fix: makeArcadeBody reference issue — imported above

export class UIManager {
  private paused: boolean = false;
  private timeScale: number = 1.0;
  private showHelp: boolean = true;
  private selectedScenario: string = 'Solar System';
  private scenarioIndex: number = 0;

  get isPaused() { return this.paused; }
  getTimeScale() { return this.timeScale; }
  getShowHelp() { return this.showHelp; }
  getSelectedScenario() { return this.selectedScenario; }
  getScenarioIndex() { return this.scenarioIndex; }

  setPaused(paused: boolean) { this.paused = paused; }
  setTimeScale(scale: number) { this.timeScale = Math.max(0.1, Math.min(10, scale)); }
  toggleHelp() { this.showHelp = !this.showHelp; }
  cycleScenario() {
    this.scenarioIndex = (this.scenarioIndex + 1) % SCENARIOS.length;
    this.selectedScenario = SCENARIOS[this.scenarioIndex].name;
  }
  loadScenario(index?: number) {
    if (index !== undefined) {
      this.scenarioIndex = index;
    }
    this.selectedScenario = SCENARIOS[this.scenarioIndex].name;
    return SCENARIOS[this.scenarioIndex].create();
  }

  draw(ctx: CanvasRenderingContext2D, w: number, h: number, bodyCount: number) {
    drawHUD(ctx, w, h, {
      paused: this.paused,
      timeScale: this.timeScale,
      bodyCount,
      showHelp: this.showHelp,
      selectedScenario: this.selectedScenario,
    });
  }
}
